"use client";

import {
	MutationCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "next-themes";
import { PostHogProvider } from "posthog-js/react";
import { useMemo, useEffect } from "react";
import { toast } from "sonner";

import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { AppConfigProvider } from "@/lib/config";

import type { AppConfig } from "@/lib/config-server";
import type { PostHogConfig } from "posthog-js";
import type { ReactNode } from "react";

interface ProvidersProps {
	children: ReactNode;
	config: AppConfig;
}

function extractErrorMessage(error: unknown): string {
	// openapi-fetch throws the parsed JSON response body directly
	// (not an Error instance) for non-OK responses.
	// API error shape: { error: true, status: number, message: string }
	const err = error as Record<string, unknown> | undefined;
	return (
		(typeof err?.message === "string" && err.message) ||
		(error instanceof Error ? error.message : "An unknown error occurred.")
	);
}

export function Providers({ children, config }: ProvidersProps) {
	const queryClient = useMemo(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						staleTime: 5 * 60 * 1000, // 5 minutes
						retry: false,
					},
				},
				mutationCache: new MutationCache({
					onError: (error) => {
						toast.error(extractErrorMessage(error));
					},
				}),
			}),
		[],
	);

	const posthogOptions: Partial<PostHogConfig> | undefined = {
		api_host: config.posthogHost,
		capture_pageview: "history_change",
		autocapture: true,
	};

	// Set up Crisp if configured
	useEffect(() => {
		const crispId = config.crispId;
		if (crispId) {
			// Dynamically import Crisp to avoid SSR issues
			void import("crisp-sdk-web").then(({ Crisp }) => {
				Crisp.configure(crispId);
			});
		}
	}, [config.crispId]);

	return (
		<AppConfigProvider config={config}>
			<ThemeProvider
				attribute="class"
				defaultTheme="system"
				enableSystem
				storageKey="theme"
			>
				<QueryClientProvider client={queryClient}>
					{config.posthogKey ? (
						<PostHogProvider
							apiKey={config.posthogKey}
							options={posthogOptions}
						>
							{children}
						</PostHogProvider>
					) : (
						children
					)}
					{process.env.NODE_ENV === "development" && (
						<ReactQueryDevtools buttonPosition="bottom-right" />
					)}
				</QueryClientProvider>
				<Toaster />
				<SonnerToaster />
			</ThemeProvider>
		</AppConfigProvider>
	);
}
